import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { API } from "@/AuthContext";
import AuctionCard from "@/components/AuctionCard";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, SlidersHorizontal, Search, X } from "lucide-react";

export default function AuctionsPage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [filters, setFilters] = useState({ brand: "", city: "", min_price: "", max_price: "", year_from: "", year_to: "", fuel: "", transmission: "" });
  const [sort, setSort] = useState("newest");

  const load = async (overrideQ) => {
    setLoading(true);
    const p = { sort };
    const activeQ = overrideQ !== undefined ? overrideQ : q;
    if (activeQ) p.q = activeQ;
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    const { data } = await axios.get(`${API}/auctions`, { params: p });
    setItems(data.items);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);
  useEffect(() => {
    const urlQ = params.get("q") || "";
    setQ(urlQ);
    load(urlQ);
    // eslint-disable-next-line
  }, [params.get("q")]);

  const submitSearch = (e) => {
    e.preventDefault();
    const trimmed = q.trim();
    if (trimmed) setParams({ q: trimmed }); else setParams({});
    load(trimmed);
  };

  const clearSearch = () => { setQ(""); setParams({}); load(""); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 fade-in">
      <Seo
        title={q ? `${q} axtarışı` : "Auksionlar"}
        description={q
          ? `"${q}" üzrə ${items.length} avtomobil auksionu. Marka, model, şəhər üzrə filtr. AzeAuksion.az.`
          : "Azərbaycanda aktiv avtomobil auksionları. Marka, model, qiymət və şəhər üzrə axtarış. Real-time təkliflər."}
        path={q ? `/auctions?q=${encodeURIComponent(q)}` : "/auctions"}
      />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">Auksionlar</h1>
          <p className="text-slate-500 mt-1">{items.length} nəticə tapıldı{q && <> — "<span className="font-semibold text-slate-700">{q}</span>" üzrə</>}</p>
        </div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-slate-500" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-52 rounded-xl h-11" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Ən yeni</SelectItem>
              <SelectItem value="ending_soon">Tezliklə bitən</SelectItem>
              <SelectItem value="price_asc">Qiymət: aşağıdan yuxarı</SelectItem>
              <SelectItem value="price_desc">Qiymət: yuxarıdan aşağı</SelectItem>
              <SelectItem value="most_bids">Ən çox təklif</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <form onSubmit={submitSearch} className="mb-8 flex gap-2">
        <div className="flex-1 relative">
          <Search className="h-5 w-5 text-slate-400 absolute left-5 top-1/2 -translate-y-1/2" />
          <Input data-testid="page-search-input" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Marka, model, şəhər və ya təsvir üzrə axtar..."
            className="h-12 pl-14 pr-12 rounded-full border-slate-300 text-base" />
          {q && (
            <button type="button" onClick={clearSearch} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="Təmizlə">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button type="submit" data-testid="page-search-submit" className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-7 font-semibold">Axtar</Button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        <aside className="bg-white border border-slate-200 rounded-2xl p-5 h-fit lg:sticky lg:top-24">
          <div className="flex items-center gap-2 mb-4"><Filter className="h-4 w-4" /><span className="font-bold text-slate-900">Filtrlər</span></div>
          <div className="space-y-3">
            <F label="Marka"><Input data-testid="filter-brand" value={filters.brand} onChange={e => setFilters({...filters, brand: e.target.value})} placeholder="BMW, Mercedes..." className="rounded-xl h-11" /></F>
            <F label="Şəhər"><Input data-testid="filter-city" value={filters.city} onChange={e => setFilters({...filters, city: e.target.value})} placeholder="Bakı, Gəncə..." className="rounded-xl h-11" /></F>
            <div className="grid grid-cols-2 gap-2">
              <F label="Min qiymət"><Input data-testid="filter-min-price" type="number" value={filters.min_price} onChange={e => setFilters({...filters, min_price: e.target.value})} className="rounded-xl h-11" /></F>
              <F label="Max qiymət"><Input data-testid="filter-max-price" type="number" value={filters.max_price} onChange={e => setFilters({...filters, max_price: e.target.value})} className="rounded-xl h-11" /></F>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <F label="İl (min)"><Input type="number" value={filters.year_from} onChange={e => setFilters({...filters, year_from: e.target.value})} className="rounded-xl h-11" /></F>
              <F label="İl (max)"><Input type="number" value={filters.year_to} onChange={e => setFilters({...filters, year_to: e.target.value})} className="rounded-xl h-11" /></F>
            </div>
            <F label="Yanacaq">
              <Select value={filters.fuel || "any"} onValueChange={v => setFilters({...filters, fuel: v === "any" ? "" : v})}>
                <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Hamısı" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Hamısı</SelectItem>
                  <SelectItem value="Benzin">Benzin</SelectItem>
                  <SelectItem value="Dizel">Dizel</SelectItem>
                  <SelectItem value="Qaz">Qaz</SelectItem>
                  <SelectItem value="Hibrid">Hibrid</SelectItem>
                  <SelectItem value="Elektrik">Elektrik</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Ötürücü qutusu">
              <Select value={filters.transmission || "any"} onValueChange={v => setFilters({...filters, transmission: v === "any" ? "" : v})}>
                <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Hamısı" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Hamısı</SelectItem>
                  <SelectItem value="Mexanika">Mexanika</SelectItem>
                  <SelectItem value="Avtomat">Avtomat</SelectItem>
                  <SelectItem value="Robot">Robot</SelectItem>
                  <SelectItem value="Variator">Variator</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <Button data-testid="apply-filters" onClick={load} className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-11 font-semibold">Filtrlə</Button>
            <Button data-testid="reset-filters" onClick={() => { setFilters({brand:"",city:"",min_price:"",max_price:"",year_from:"",year_to:"",fuel:"",transmission:""}); setTimeout(load, 100);}} variant="ghost" className="w-full rounded-full h-11">Sıfırla</Button>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => <div key={i} className="aspect-[4/3] rounded-2xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-24 border-2 border-dashed border-slate-200 rounded-2xl">
              <p className="text-slate-500 font-semibold">Bu filtrlərə uyğun auksion tapılmadı</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {items.map(a => <AuctionCard key={a.id} a={a} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
